import {
  SEVERITY,
  ADVICE_STATUS,
  CHECKLIST_DONE,
  CHECKLIST_WORK,
  SALES_SEVERITY,
  CHECKLIST_REMINDER_SCHEDULED,
} from 'constants/ChecklistConstants';
import { ANONYMOUS } from 'constants/GDPRConstants';
import { ALL_EVENTS, STATUSES } from 'constants/InterventionConstants';
import { IMAGE_TYPE, VIDEO_TYPE } from 'constants/MediaConstants';
import {
  MODULES,
  STOCK_VO,
  BODYWORK_APPOINTMENT,
  getDefaultModulesByMode,
} from 'constants/ModuleConstants';
import {
  AFTER_SALES_MANAGER,
  LEADERSHIP_ROLES, PARTS_MANAGER,
  READ_ONLY,
  SALES_MANAGER,
  SELF_SERVICE_MANAGER
} from 'constants/UserConstants';

import { hasItems } from 'services/ChecklistUtils';

import { hasAccess } from '../../../common/utils/hasAccess';

export const canSave = (intervention, canAccessToDealer) => {
  const { status, statusGDPR } = intervention;
  if (canAccessToDealer === false) {
    return false;
  }
  return (
    [STATUSES.OPEN, STATUSES.TO_BE_SENT, STATUSES.SENT, STATUSES.READ, STATUSES.ANSWERED].includes(status) &&
    statusGDPR !== ANONYMOUS
  );
};

export const canConvertToBodywork = ({ mode, module }) => mode !== STATUSES.CLOSED && module === BODYWORK_APPOINTMENT;

export const canClone = ({ status, mode, module }) =>
  status !== STATUSES.CLOSED && getDefaultModulesByMode(mode).includes(module);

export const canApproveManually = ({ module, status: interventionStatus, checklists = [] }) => {
  const hasPendingAdvices =
    checklists?.some(({ items, type }) => {
      // Users cannot accept advices in this checklist
      if (type === CHECKLIST_DONE) {
        return false;
      }

      return (
        items?.some(
          ({ advices }) =>
            advices?.some(
              ({ status, severity }) =>
                status === ADVICE_STATUS.PENDING &&
                (severity === SEVERITY.HIGH ||
                  severity === SEVERITY.LOW ||
                  severity === SALES_SEVERITY.PROPOSED ||
                  severity === SALES_SEVERITY.OFFERED)
            ) ?? false
        ) ?? false
      );
    }) ?? false;

  const { canAcceptManually = false } = MODULES[module] ?? {};
  const isClosedOrDeleted = interventionStatus === STATUSES.CLOSED || interventionStatus === STATUSES.DELETED;

  return !isClosedOrDeleted && canAcceptManually && hasPendingAdvices;
};

export const canReOpen = (intervention, userRoles) =>
  intervention.status === STATUSES.CLOSED &&
  !userRoles.includes(READ_ONLY) &&
  typeof intervention.statusBeforeClose !== 'undefined';

export const canDelete = userRoles =>
  hasAccess(userRoles, [...LEADERSHIP_ROLES, SALES_MANAGER, AFTER_SALES_MANAGER, PARTS_MANAGER, SELF_SERVICE_MANAGER]) && !userRoles.includes(READ_ONLY);

export const canEdit = ({ status }) => ![STATUSES.CLOSED].includes(status);

const hasEventAlreadyBeenAnswered = (events = []) =>
  events.some(e => [ALL_EVENTS.ACCEPTED, ALL_EVENTS.REJECTED].includes(e.event));

export const canDisplayAcceptedTotal = ({ status, events }, type, checklist, acceptManually = false) => {
  const hasRightStatus = [STATUSES.ANSWERED, STATUSES.CLOSED].includes(status);
  const hasBeenAnswered = hasEventAlreadyBeenAnswered(events);
  const hasChecklistOk = type !== CHECKLIST_DONE && hasItems(checklist);
  return acceptManually || (hasRightStatus && hasBeenAnswered && hasChecklistOk);
};

export const canDisplayAcceptTags = ({ status, events }, advice) => {
  const hasRightStatus = [STATUSES.ANSWERED, STATUSES.CLOSED].includes(status);
  const hasBeenAnswered = hasEventAlreadyBeenAnswered(events);
  const hasChecklistOk = [CHECKLIST_WORK, CHECKLIST_REMINDER_SCHEDULED].includes(advice.fromType);
  return hasRightStatus && hasBeenAnswered && hasChecklistOk;
};

export const haveGlobalMedias = ({ medias, checklists = [] }) => {
  const haveInterventionMedias = medias?.length > 0;
  const haveChecklistsMedias = checklists.some(({ items = [] }) =>
    items.some(({ advices = [] }) => advices.some(({ medias: mediasAdvice }) => mediasAdvice?.length > 0)));
  return haveInterventionMedias || haveChecklistsMedias;
};

export const hasGlobalImageOrVideos = ({ medias, checklists = [] }) => {
  const hasMedia = medias?.some(({ type }) => type === IMAGE_TYPE || type === VIDEO_TYPE) ?? false;
  const hasMediaInChecklists =
    checklists?.some(({ items }) =>
      // eslint-disable-next-line no-shadow
      items?.some(({ advices }) => advices?.some(({ medias }) => medias?.some(({ type }) => type === IMAGE_TYPE || type === VIDEO_TYPE)))) ?? false;

  return hasMediaInChecklists || hasMedia;
};

// STOCK_VO has a status ALL_STATUS.OPEN, AUDIT has a status ALL_STATUS.OPEN too but the color used is not the same
export const getStyleByStatus = (module, status) => {
  let style = status?.toLowerCase();
  if (module === STOCK_VO && status === STATUSES.OPEN) {
    style = `${STOCK_VO.toLowerCase()}_${style}`;
  }
  return style;
};

export const countPublicVideos = intervention =>
  intervention?.medias?.filter(media => media.type === VIDEO_TYPE && !media.restricted)?.length ?? 0;
